import { useEffect, useRef, useState } from "react";
import { sfx } from "../../lib/sound";
import { noteFor } from "../../lib/lock";
import { DEVICE_LABEL } from "../../lib/console";
import { LockBrief } from "./LockBrief";

const STEP_MS = 1200;
const LIT_MS = 800;
const BRIEF_MS = 12000;
const DEBOUNCE_MS = 350;

// Simon-style: the hive lights a run of push buttons, then the audience repeats it on the boxes.
// Switches, dials and the confirm button are ignored while repeating; bounces are debounced.
export const LockSequence = ({ makeSteps, length, giveIn, lastEvent, showHint, attempt, onProgress, onResult }) => {
  const [steps, setSteps] = useState([]);
  const [phase, setPhase] = useState("brief");
  const [cursor, setCursor] = useState(-1);
  const [entered, setEntered] = useState(0);
  const [got, setGot] = useState(null);
  const [miss, setMiss] = useState(null);
  const seen = useRef(null);
  const lastPress = useRef({ id: null, at: 0 });
  const timers = useRef([]);
  const stepsRef = useRef(steps);
  stepsRef.current = steps;

  const clearTimers = () => { timers.current.forEach(clearTimeout); timers.current = []; };

  const startShow = (list) => {
    clearTimers();
    setPhase("show");
    setEntered(0);
    setMiss(null);
    setGot(null);
    onProgress(0);
    const slow = showHint ? 1.3 : 1;
    list.forEach((s, idx) => {
      const at = 700 + idx * STEP_MS * slow;
      timers.current.push(setTimeout(() => { setCursor(idx); sfx.step(noteFor(s.id)); }, at));
      timers.current.push(setTimeout(() => setCursor(-1), at + LIT_MS * slow));
    });
    timers.current.push(setTimeout(() => { setCursor(-1); setPhase("input"); }, 700 + list.length * STEP_MS * slow + 200));
  };

  const begin = () => {
    if (phase !== "brief") return;
    const list = makeSteps();
    if (!list.length) { setPhase("done"); onResult(true); return; }
    setSteps(list);
    startShow(list);
  };

  useEffect(() => { if (attempt > 1) startShow(stepsRef.current); }, [attempt]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => clearTimers, []);

  useEffect(() => {
    if (!lastEvent || lastEvent === seen.current) return;
    seen.current = lastEvent;
    if (phase === "brief") { if (lastEvent.type === "press" || lastEvent.type === "confirm") begin(); return; }
    if (phase !== "input" || lastEvent.type !== "press") return;
    const now = lastEvent.t || Date.now();
    if (lastPress.current.id === lastEvent.id && now - lastPress.current.at < DEBOUNCE_MS) return;
    lastPress.current = { id: lastEvent.id, at: now };
    setGot(lastEvent.name);
    const expected = steps[entered];
    if (lastEvent.id === expected.id) {
      sfx.step(noteFor(expected.id));
      const n = entered + 1;
      setEntered(n);
      onProgress(n / steps.length);
      if (n === steps.length) { setPhase("done"); onResult(true); }
    } else {
      setMiss({ got: lastEvent.name, wanted: expected.name });
      setPhase("done");
      onResult(false);
    }
  }, [lastEvent]); // eslint-disable-line react-hooks/exhaustive-deps

  if (phase === "brief") {
    return (
      <div data-testid="lock-sequence" data-phase={phase}>
        <LockBrief
          title="Repeat the path"
          ms={BRIEF_MS}
          onDone={begin}
          lines={[
            `The hive will light up a path of ${length} console buttons, one at a time, each with its own tone.`,
            "Watch and remember the order - the names fade once the path has played.",
            "When it says YOUR TURN, press the same push buttons in the same order. Leave the switches and dials alone.",
            `A wrong press makes the hive replay the path. After two misses the names stay on screen; after ${giveIn} the hive lets the memory go.`,
          ]}
        />
      </div>
    );
  }

  return (
    <div data-testid="lock-sequence" data-phase={phase} className="grid gap-5">
      <p data-testid="lock-instruction" className="font-mono text-sm uppercase tracking-[0.25em] text-cyan-200">
        {phase === "show" ? "Watch. The hive is remembering the path…" : "Your turn - repeat the path on the console"}
      </p>
      <ol className="grid gap-2 sm:grid-cols-2">
        {steps.map((s, idx) => {
          const lit = cursor === idx;
          const done = phase !== "show" && idx < entered;
          const next = phase === "input" && idx === entered;
          const visible = phase === "show" ? idx <= cursor : showHint || done;
          return (
            <li
              key={`${s.id}-${idx}`}
              data-testid={`lock-step-${idx + 1}`}
              data-state={lit ? "lit" : done ? "done" : next ? "next" : "idle"}
              className={`flex items-center gap-3 border px-4 py-3 font-mono text-sm uppercase tracking-widest transition-all duration-200 ${
                lit ? "border-cyan-300 bg-cyan-300/20 text-white shadow-[0_0_24px_rgba(34,211,238,0.45)] scale-[1.02]"
                  : done ? "border-emerald-400/60 bg-emerald-400/10 text-emerald-200"
                    : next ? "border-amber-300/60 text-amber-200 animate-pulse"
                      : visible ? "border-slate-700 text-slate-300" : "border-slate-800 text-slate-600"}`}
            >
              <span className="w-6 text-xs opacity-70">{String(idx + 1).padStart(2, "0")}</span>
              <span className="flex-1">{visible ? s.name : "· · ·"}</span>
              {visible && <span className="text-[10px] opacity-80">PRESS · {DEVICE_LABEL[s.device]}</span>}
            </li>
          );
        })}
      </ol>
      {miss ? (
        <p data-testid="lock-mismatch" className="font-mono text-xs uppercase tracking-widest text-rose-300">
          Console registered <span className="text-white">{miss.got}</span> · the path wanted <span className="text-white">{miss.wanted}</span>
        </p>
      ) : (
        <p data-testid="lock-registered" className="font-mono text-xs uppercase tracking-widest text-slate-500">
          Console registered: <span className="text-slate-200">{phase === "input" && got ? got : "—"}</span>
        </p>
      )}
    </div>
  );
};
